<template>
  <el-container class="app-wrapper">
    <el-aside :width="isCollapse ? '64px' : '220px'" class="sidebar-container">
      <div class="logo">
        <span v-show="!isCollapse">安小供阳光采供平台</span>
        <span v-show="isCollapse">安小供</span>
      </div>
      <el-menu
        ref="sidebarMenu"
        :default-active="activeMenu"
        :collapse="isCollapse"
        :collapse-transition="false"
        router
        background-color="#304156"
        text-color="#bfcbd9"
        active-text-color="#409EFF"
      >
        <template v-for="route in routes">
          <el-submenu v-if="route.children && route.children.length > 1" :key="route.path" :index="route.path">
            <template #title>
              <i :class="route.meta?.icon || 'el-icon-menu'"></i>
              <span>{{ getMenuTitle(route) }}</span>
            </template>
            <el-menu-item v-for="child in route.children" :key="child.path" :index="getMenuIndex(route, child)">
              {{ getMenuTitle(child) }}
            </el-menu-item>
          </el-submenu>
          <el-menu-item v-else-if="route.children" :key="route.path" :index="getMenuIndex(route, route.children[0])">
            <i :class="route.children[0].meta?.icon || route.meta?.icon || 'el-icon-menu'"></i>
            <span>{{ getMenuTitle(route.children[0]) || getMenuTitle(route) }}</span>
          </el-menu-item>
        </template>
        <!-- 大屏数据菜单（教育端） -->
        <el-menu-item v-if="isEduUser" @click="openBigScreen">
          <i class="el-icon-monitor"></i>
          <span>大屏数据</span>
        </el-menu-item>
      </el-menu>
    </el-aside>
    <el-container>
      <el-header class="app-header">
        <div class="header-left">
          <i :class="isCollapse ? 'el-icon-s-unfold' : 'el-icon-s-fold'" @click="toggleCollapse"></i>
        </div>
        <div class="header-right">
          <el-dropdown>
            <span class="user-info">
              <span v-if="orgName" class="org-name">{{ orgName }}</span>
              <i class="el-icon-user-solid"></i>
              {{ realName || username || '用户' }}
              <i class="el-icon-arrow-down"></i>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click.native="pwdDialogVisible = true">修改密码</el-dropdown-item>
                <el-dropdown-item divided @click.native="handleLogout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </el-header>
      <el-main :class="['app-main', isHome ? 'home-main' : '']">
        <router-view :key="$route.path"></router-view>
      </el-main>
    </el-container>

    <!-- 修改密码弹窗 -->
    <el-dialog title="修改密码" :visible.sync="pwdDialogVisible" width="400px" @closed="resetPwdForm">
      <el-form ref="pwdForm" :model="pwdForm" :rules="pwdRules" label-width="80px">
        <el-form-item label="旧密码" prop="oldPassword">
          <el-input v-model="pwdForm.oldPassword" type="password" placeholder="请输入旧密码" show-password></el-input>
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="pwdForm.newPassword" type="password" placeholder="请输入新密码" show-password></el-input>
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="pwdForm.confirmPassword" type="password" placeholder="请再次输入新密码" show-password></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="pwdDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="pwdLoading" @click="handleChangePassword">确认</el-button>
      </div>
    </el-dialog>

    <!-- 全局：逾期未填报查验信息提示弹窗（供货商端） -->
    <OverdueInspectionDialog
      :visible.sync="overdueDialogVisible"
      :list="overdueInspections"
      :overdue-hours-limit="72"
    />
  </el-container>
</template>

<script>
import { changePassword } from '@/api/auth'
import { getPurchaseVisiblePrices, getEduVisiblePrices } from '@/api/sysConfig'
import OverdueInspectionDialog from '@/components/OverdueInspectionDialog.vue'

export default {
  name: 'Layout',
  components: { OverdueInspectionDialog },
  data() {
    const validateConfirm = (rule, value, callback) => {
      if (!value) return callback(new Error('请确认新密码'))
      if (value !== this.pwdForm.newPassword) return callback(new Error('两次输入的密码不一致'))
      callback()
    }
    return {
      isCollapse: false,
      username: localStorage.getItem('username'),
      realName: localStorage.getItem('realName'),
      orgName: localStorage.getItem('supplierName') || localStorage.getItem('unitName') || '',
      pwdDialogVisible: false,
      pwdLoading: false,
      pwdForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      pwdRules: {
        oldPassword: [{ required: true, message: '请输入旧密码', trigger: 'blur' }],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, max: 20, message: '密码长度为6-20个字符', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, validator: validateConfirm, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    // 全局逾期弹窗：显隐与数据均取自 Vuex，由路由守卫触发
    overdueDialogVisible: {
      get() {
        return this.$store.state.overdueDialogVisible
      },
      set(val) {
        this.$store.commit('SET_OVERDUE_DIALOG_VISIBLE', val)
      }
    },
    overdueInspections() {
      return this.$store.state.overdueInspections || []
    },
    routes() {
      const userType = Number(localStorage.getItem('userType') || 0)
      const district = localStorage.getItem('district') || ''
      const districtCode = localStorage.getItem('districtCode') || ''
      // 市级账号：平台监管(1)/集采平台(5) 且区县为空（绑定了区县级平台单位的平台监管也不算市级）
      const isCityLevel = (userType === 1 || userType === 5) && !district && !districtCode
      const enableBid = localStorage.getItem('enableBid')
      return this.$router.options.routes
        .filter(r => !r.hidden)
        // 首页等菜单按角色隐藏（hideMenuFor）：供货商(2)、采购单位(3) 不显示"首页"入口
        .filter(r => !(r.meta && r.meta.hideMenuFor && r.meta.hideMenuFor.includes(userType)))
        // 区县竞价开关：enableBid === '0' 时隐藏整个竞价（在线议价）模块；空串/缺失不隐藏（兼容旧会话）
        .filter(r => !(r.path === '/bid-bargain' && enableBid === '0'))
        .map(r => ({
          ...r,
          children: r.children ? r.children.filter(c => {
            if (c.hidden) return false
            const roles = c.meta?.roles || []
            if (!roles.includes(userType)) return false
            // cityOnly：仅市级单位可见（如平台单位管理）
            if (c.meta?.cityOnly && !isCityLevel) return false
            return true
          }) : []
        }))
        .filter(r => {
          const parentRoles = r.meta?.roles || []
          if (!parentRoles.includes(userType)) return false
          return r.children && r.children.length > 0
        })
    },
    activeMenu() {
      return this.$route.path
    },
    isHome() {
      return this.$route.path === '/home' || this.$route.path === '/mall/index'
    },
    isEduUser() {
      return Number(localStorage.getItem('userType') || 0) === 5
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          const menu = this.$refs.sidebarMenu
          if (menu) {
            menu.activeIndex = this.activeMenu
          }
        })
      }
    }
  },
  created() {
    this.loadPriceConfig()
    this.checkOverdueOnEnter()
  },
  methods: {
    /**
     * 供货商进入系统时（登录/F5/直输URL）：存在逾期未填报查验信息的订单，
     * 立即弹出强制填报引导弹窗（弹窗不可关闭，只能点「去填报」）。
     */
    checkOverdueOnEnter() {
      if (Number(localStorage.getItem('userType') || 0) !== 2) return
      this.$store.dispatch('fetchOverdueInspections').then(list => {
        if (list && list.length > 0 && !this.$store.state.overdueDialogVisible) {
          this.$store.commit('SET_OVERDUE_DIALOG_VISIBLE', true)
        }
      })
    },
    async loadPriceConfig() {
      try {
        const [p, e] = await Promise.all([
          getPurchaseVisiblePrices(),
          getEduVisiblePrices()
        ])
        localStorage.setItem('purchaseVisiblePrices', JSON.stringify(p.data || []))
        localStorage.setItem('eduVisiblePrices', JSON.stringify(e.data || []))
      } catch (err) {
        // 失败时使用默认值，不影响布局加载
        if (!localStorage.getItem('purchaseVisiblePrices')) {
          localStorage.setItem('purchaseVisiblePrices', '["price"]')
        }
        if (!localStorage.getItem('eduVisiblePrices')) {
          localStorage.setItem('eduVisiblePrices', '["price","basePrice"]')
        }
      }
    },
    getMenuTitle(route) {
      if (!route || !route.meta) return ''
      const userType = Number(localStorage.getItem('userType') || 0)
      const titleMap = route.meta.titleMap
      if (titleMap && titleMap[userType]) {
        return titleMap[userType]
      }
      return route.meta.title
    },
    getMenuIndex(parentRoute, childRoute) {
      // 去掉路径参数，去掉开头的 /
      const childPath = childRoute.path.replace(/\/:.*$/, '').replace(/^\/+/, '')
      const basePath = parentRoute.path === '/' ? '' : parentRoute.path
      let index = basePath + '/' + childPath
      if (index === '/bid-bargain/supplier/countdown') {
        index = '/bid-bargain/supplier/countdown'
      }
      return index.startsWith('/') ? index : '/' + index
    },
    toggleCollapse() {
      this.isCollapse = !this.isCollapse
    },
    openBigScreen() {
      const token = localStorage.getItem('token')
      const url = window.location.origin + '/#/screen'
      window.open(url, '_blank')
    },
    handleLogout() {
      const lastSelectedRole = localStorage.getItem('lastSelectedRole')
      localStorage.clear()
      if (lastSelectedRole) {
        localStorage.setItem('lastSelectedRole', lastSelectedRole)
      }
      // 清空全局逾期查验缓存，避免换账号登录后沿用上一个账号的数据
      this.$store.commit('CLEAR_OVERDUE_INSPECTIONS')
      this.$router.push('/login')
    },
    async handleChangePassword() {
      try {
        const valid = await this.$refs.pwdForm.validate()
        if (!valid) return
        this.pwdLoading = true
        await changePassword({
          oldPassword: this.pwdForm.oldPassword,
          newPassword: this.pwdForm.newPassword
        })
        this.$message.success('密码修改成功，请重新登录')
        this.pwdDialogVisible = false
        setTimeout(() => this.handleLogout(), 800)
      } catch (e) {
        this.$message.error(e.message || '修改失败')
      } finally {
        this.pwdLoading = false
      }
    },
    resetPwdForm() {
      this.pwdForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
      this.$refs.pwdForm && this.$refs.pwdForm.resetFields()
    }
  }
}
</script>

<style lang="scss" scoped>
.app-wrapper {
  height: 100vh;
}
.sidebar-container {
  background-color: #304156;
  overflow: hidden;
  .logo {
    height: 60px;
    line-height: 60px;
    text-align: center;
    color: #fff;
    font-size: 18px;
    font-weight: bold;
    background-color: #263445;
  }
  ::v-deep .el-menu {
    border: none;
  }
}
.app-header {
  background-color: #fff;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  .header-left {
    i {
      font-size: 20px;
      cursor: pointer;
    }
  }
  .user-info {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
    .org-name {
      font-size: 13px;
      color: #606266;
      background-color: #f4f4f5;
      padding: 2px 8px;
      border-radius: 4px;
      margin-right: 4px;
    }
  }
}
.app-main {
  background-color: #f0f2f5;
  padding: 20px;
}
.home-main {
  padding: 0;
  background-color: transparent;
  overflow-y: auto;
}
</style>
